import { useEffect, useState } from "react";
import { api, RAYG, RAYG_TEXT, EVIDENCE } from "../lib/api";
import { PageHeader } from "../components/Layout";

export default function Capabilities() {
  const [caps, setCaps] = useState([]);
  const [depts, setDepts] = useState([]);
  const [view, setView] = useState("ai");

  useEffect(() => {
    Promise.all([api.get("/capabilities"), api.get("/departments")]).then(([c, d]) => { setCaps(c.data); setDepts(d.data); });
  }, []);

  const deptByKey = Object.fromEntries(depts.map((d) => [d.key, d]));
  const grouped = depts.map((d) => ({ dept: d, caps: caps.filter((c) => c.department_key === d.key) })).filter((g) => g.caps.length);

  const heatValue = (c) => (view === "ai" ? c.ai_opportunity : Math.round(((c.maturity_current) / 5) * 100));

  return (
    <div className="canvas-grain min-h-screen pb-16" data-testid="capabilities-page">
      <PageHeader overline="Business Reference Model" title="Capability Heat Map"
        subtitle="Industry reference taxonomy, editable per organisation. Toggle between AI opportunity and current maturity heat."
        actions={
          <div className="flex border border-black/15 rounded-sm overflow-hidden text-xs">
            {[["ai", "AI opportunity"], ["maturity", "Current maturity"]].map(([k, l]) => (
              <button key={k} onClick={() => setView(k)} data-testid={`heatmap-toggle-${k}`}
                className={`px-3 py-2 transition-colors ${view === k ? "bg-[#0A0A0A] text-[#D4AF37]" : "bg-white hover:bg-black/5"}`}>{l}</button>
            ))}
          </div>
        } />
      <div className="px-8 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {grouped.map(({ dept, caps }) => (
          <div key={dept.key} className="bg-white border border-black/10 rounded-sm overflow-hidden" data-testid={`capability-group-${dept.key}`}>
            <div className="px-4 py-2.5 text-white font-display font-bold text-sm tracking-tight" style={{ background: dept.color }}>{dept.name}</div>
            <div className="p-3 space-y-2">
              {caps.map((c) => {
                const v = heatValue(c);
                return (
                  <div key={c.id} className="chip flex items-center justify-between border border-black/8 rounded-sm px-3 py-2" data-testid="capability-card">
                    <div>
                      <p className="text-[13px] font-medium">{c.name}</p>
                      <div className="flex items-center gap-2 text-[10px] text-black/50">
                        <span>maturity {c.maturity_current} → {c.maturity_target}</span>
                        <span style={{ color: EVIDENCE[c.evidence_status]?.color }}>● {EVIDENCE[c.evidence_status]?.label}</span>
                      </div>
                    </div>
                    <span className="text-[12px] font-mono font-medium px-2 py-1 rounded-sm shrink-0" style={{ background: RAYG(v), color: RAYG_TEXT(v) }}>
                      {v}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <div className="px-8 mt-6 flex gap-4 text-[11px] text-black/60">
        {[["#EF4444", "Red < 40"], ["#F59E0B", "Amber 40–59"], ["#FDE047", "Yellow 60–74"], ["#22C55E", "Green ≥ 75"]].map(([c, l]) => (
          <span key={l} className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: c }} />{l}</span>
        ))}
        <span className="ml-4">Evidence: {Object.values(EVIDENCE).map((e) => e.label).join(" · ")}</span>
      </div>
    </div>
  );
}
